/**
 * The app's route boundary (BACKLOG dashboard-webgl, a route's code that fails to load): a page that throws while
 * it loads or renders shows a plain-words offer to reload in its place, and the app around it (sidebar, header)
 * stays. Without it, React unmounted the whole app.
 *
 * The usual cause is a frontend deploy while a tab is open: the tab's bundle names the old hash of each page's
 * code, nginx answers that file 404, and the page's lazy import rejects. That case says AskWingo was updated
 * (offline, where the browser gives the same message, that the page couldn't load offline); any other error says
 * the page hit a problem. Either way, Reload loads the page again. The boundary clears when
 * the path changes, so another page opens as usual.
 *
 * The first time the boundary catches a page whose code did not load, it reloads the tab by itself, which brings in
 * the new version without a click; a second within a minute leaves the offer on screen (no reload loop). Only a
 * page's own failure reloads: Vite's `vite:preloadError` fires for any lazy code (the document viewers' too, which
 * their own boundary handles in the viewer's pane), so the tab never reloads on it.
 */
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Component } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const STALE_CODE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;
const RELOADED_AT = 'askwingo.reloadedForNewVersionAt';
const RELOAD_WINDOW_MS = 60_000;

/** The error, or any error it was caused by, is a page's code that did not load (Chrome, Firefox, Safari, Vite). */
export function isStaleCodeError(error: unknown): boolean {
  for (let e: unknown = error, depth = 0; e && depth < 5; e = (e as { cause?: unknown }).cause, depth += 1) {
    if (STALE_CODE.test(String((e as { message?: unknown }).message ?? e))) return true;
  }
  return false;
}

/** Reload the tab once when a page's code did not load; never again within a minute (no reload loop), and never
 * without session storage to hold that guard. */
function reloadOnceForNewVersion(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    return; // no session storage (blocked): without it a reload could loop, so the offer stays instead
  }
  window.location.reload();
}

type Failure = 'stale' | 'offline' | 'other';

const WORDS: Record<Failure, [string, string]> = {
  stale: ['AskWingo was updated while this page was open', 'Reload to get the latest version.'],
  offline: ["This page couldn't load while you're offline", 'Reload once you are back online.'],
  other: ['This page hit a problem', 'Reload to try again.'],
};

/** What failed: a page's code that did not load (offline, the browser gives the same message, so it says that
 * instead), or anything else. */
function failureOf(error: unknown): Failure {
  if (!isStaleCodeError(error)) return 'other';
  return navigator.onLine === false ? 'offline' : 'stale';
}

const ReloadOffer: React.FC<{ failure: Failure }> = ({ failure }) => (
  <div
    data-testid="route-load-failed"
    role="alert"
    className="flex h-full min-h-[70vh] flex-col items-center justify-center px-6 text-center"
  >
    <p className="text-balance text-base font-medium text-slate-200">{WORDS[failure][0]}</p>
    <p className="mt-2 max-w-md text-balance text-sm text-slate-400">{WORDS[failure][1]}</p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/20 hover:bg-white/10"
    >
      <ArrowPathIcon aria-hidden="true" className="h-4 w-4" />
      Reload
    </button>
  </div>
);

interface BoundaryProps {
  resetKey: string;
  children: ReactNode;
}

class Boundary extends Component<BoundaryProps, { error: unknown; failure: Failure }> {
  state: { error: unknown; failure: Failure } = { error: null, failure: 'other' };

  static getDerivedStateFromError(error: unknown) {
    return { error: error ?? new Error('the page failed'), failure: failureOf(error) };
  }

  componentDidCatch(error: unknown) {
    console.warn('RouteBoundary: the page could not load; offering a reload.', String((error as Error)?.message ?? error));
    // offline the reload would only reach the browser's own offline page, the whole app gone
    if (failureOf(error) === 'stale') reloadOnceForNewVersion();
  }

  componentDidUpdate(previous: BoundaryProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    return this.state.error ? <ReloadOffer failure={this.state.failure} /> : this.props.children;
  }
}

/** Wraps the routes; clears itself when the path changes. */
const RouteBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
};

export default RouteBoundary;
